import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

export interface ProfileMenuItem {
  /** Stable key + visible label. */
  label: string;
  /** Optional leading glyph (e.g. a lucide icon node). */
  icon?: ReactNode;
  /** Click handler. Omit + set `disabled` for a placeholder row. */
  onSelect?: () => void;
  /** Dim + non-interactive (v35 `.profile-dropdown-item.dimmed`). */
  disabled?: boolean;
  /** Destructive styling (v35 `.profile-dropdown-item.danger`). */
  danger?: boolean;
}

export interface ProfileMenuProps {
  /** Avatar trigger content — typically the user's initials. */
  initials: string;
  /** Full name shown in the dropdown header. */
  name: string;
  /** Email shown beneath the name. */
  email: string;
  /** Optional role pill (v35 `.profile-dropdown-role`). */
  role?: string;
  /** Menu rows. Order preserved; a `null` entry renders a divider. */
  items: Array<ProfileMenuItem | null>;
  /** Optional extra classes for the avatar trigger. */
  className?: string;
}

/**
 * ProfileMenu — avatar disclosure → dropdown, surface-agnostic.
 *
 * Built on Radix `DropdownMenu` (the validated-stack standard for
 * menu/disclosure primitives): focus management, type-ahead, ESC/click-
 * outside dismissal, ARIA wiring, and Escape-to-close come for free —
 * matching v35's hand-rolled `toggleProfileMenu` behavior but
 * accessible by construction.
 *
 * Visual spec from v35 `.profile-avatar` / `.profile-dropdown*`:
 *   - 36px round accent avatar with initials (`text-label` 12px, bold)
 *   - 220px-wide surface popover, `rounded-lg` (12px), shadow-lg
 *   - header block: name (`text-h3`) + email (`text-caption`) + optional
 *     uppercase accent role pill
 *   - item rows: `text-body`, `rounded-md` (8px), accent-muted hover;
 *     `dimmed` (opacity-40, non-interactive) + `danger` (error color)
 *     variants; `null` items render a 1px divider
 *
 * Tokenized: only token utilities + Tailwind's 4px scale — no inline
 * styles, no arbitrary `text-[Npx]`. The dark-mode toggle is passed in
 * as an item by the surface (web wires it to `useTheme().toggle`).
 */
export function ProfileMenu({
  initials,
  name,
  email,
  role,
  items,
  className,
}: ProfileMenuProps) {
  const avatar =
    "inline-flex items-center justify-center w-9 h-9 rounded-full bg-accent text-bg text-label font-bold cursor-pointer outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-accent-muted hover:shadow-md";

  // Stable, content-derived keys (no array-index keys): an item keys on
  // its label; a divider keys on the label of the item before it (or
  // its ordinal position among dividers when it's leading), so React's
  // reconciliation stays correct even if items reorder.
  let lastLabel = "";
  let dividerSeq = 0;
  const keyedEntries = items.map((item) => {
    if (item === null) {
      const key = lastLabel
        ? `divider-after-${lastLabel}`
        : `divider-${dividerSeq++}`;
      return { key, item };
    }
    lastLabel = item.label;
    return { key: item.label, item };
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Open profile menu"
          data-testid="profile-avatar"
          className={[avatar, className].filter(Boolean).join(" ")}
        >
          {initials}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          data-testid="profile-dropdown"
          className="w-56 overflow-hidden rounded-lg bg-surface border border-border shadow-lg z-50"
        >
          <div className="px-4 py-3.5 border-b border-border">
            <div className="text-h3 text-text">{name}</div>
            <div className="text-caption text-text-secondary mt-0.5">
              {email}
            </div>
            {role ? (
              <span className="inline-block mt-1.5 px-2.5 py-0.5 rounded-pill bg-accent-muted text-accent text-nano uppercase tracking-wide">
                {role}
              </span>
            ) : null}
          </div>

          <div className="p-1.5">
            {keyedEntries.map(({ key, item }) => {
              if (item === null) {
                return (
                  <DropdownMenu.Separator
                    key={key}
                    className="h-px my-1 bg-border"
                  />
                );
              }

              const tone = item.danger
                ? "text-error data-[highlighted]:bg-error-muted"
                : "text-text-secondary data-[highlighted]:bg-accent-muted data-[highlighted]:text-text";
              const dimmed = item.disabled
                ? "opacity-40 cursor-default"
                : "cursor-pointer";

              return (
                <DropdownMenu.Item
                  key={key}
                  disabled={item.disabled}
                  onSelect={item.onSelect}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-body outline-none ${tone} ${dimmed}`}
                >
                  {item.icon ? (
                    <span className="inline-flex shrink-0" aria-hidden="true">
                      {item.icon}
                    </span>
                  ) : null}
                  {item.label}
                </DropdownMenu.Item>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
